import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LabelsModuleSpecsFragment } from '../../../../api/fragments/LabelsModuleSpecs';
import {
    UpdateLabelsModuleDocument,
    type UpdateLabelsModuleMutation,
    type UpdateLabelsModuleMutationVariables,
} from '../../../../api/mutations/updateLabelsModule';
import { type LabelsModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type LabelsModuleUpdateFormProps = {
    module: LabelsModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = LabelsModuleSettings;

const LabelsModuleUpdateForm = ({ module, onComplete }: LabelsModuleUpdateFormProps) => {
    const { t } = useTranslation(['labelsModule']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('labelsModule:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateLabelsModuleMutation, UpdateLabelsModuleMutationVariables>({
                    mutation: UpdateLabelsModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('labelsModule:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updatelabelsModule"
                    layout="vertical"
                    name="updatelabelsModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('labelsModule:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default LabelsModuleUpdateForm;
